import { type ClientId } from './telepointers.ts';
import rovoAvatarUrl from './ui/rovo-agent-avatar.svg';

export type AccountId = string;

/**
 * The full Identity user payload API endpoints such as /gateway/api/me
 */
export interface FullIdentityApiUserPayload {
  account_id: AccountId;
  nickname: string;
  picture: string;
  email: string;
  account_type: string;
  name: string;
  account_status: string;
  zoneinfo: string;
  locale: string;
  extended_profile: Record<string, any>;
  email_verified: boolean;
}

/**
 * To enable tagged unions for Identity types and differentiating between them at runtime
 */
export enum IdentityKind {
  CanvasId = 'canvas',
  AccountIdOnly = 'identity-account-id-only',
  AnonymousId = 'anonymous',
  AiAgent = 'ai-agent',
}

/**
 * Denotes a 'partially' loaded identity where only the accountId is known, with all other fields undefined
 */
export type IdentityAccountIdOnly = {
  accountId: AccountId;
  displayName: undefined;
  nickname: undefined;
  picture: undefined;
  email: undefined;
  locale: undefined;
  type: IdentityKind.AccountIdOnly;
};

/**
 * The set of Identity fields that Canvas depends on
 * @privateRemark We need to explicitly make a new type and still support the old type
 * kicking around in the codebase since it could’ve possibly been stored in customers' indexedDBs.
 * It made sense to extend the original interface with a new field here since that’s the only
 * meaningful difference
 */
export interface CanvasIdentity {
  type: IdentityKind.CanvasId;
  accountId: AccountId;
  /**
   * User's name, applies relevant privacy restrictions
   */
  displayName: string;
  /**
   * @deprecated use `displayName` instead as it shows the correct name depending on the user's configured privacy restrictions
   */
  nickname: string;
  picture: string;
  email?: string;
  locale: string; // eg en-US, the full locale from identity
}

/**
 * @see {@link https://developer.atlassian.com/platform/identity/learning/edge-authenticator/uct/#authentication-type-/-api-specific-claims}
 */
export const ANONYMOUS_ID = 'unidentified' as const;
export const AGENT_ID = 'ai-agent' as const;
export const AGENT_CLIENT_ID = 0xa1 as ClientId;

export interface Anonymous {
  accountId: typeof ANONYMOUS_ID;
  displayName: undefined;
  nickname: undefined;
  picture: undefined;
  email: undefined;
  locale: undefined;
  type: IdentityKind.AnonymousId;
}

export interface AiAgentIdentity {
  accountId: AccountId;
  displayName: string | undefined;
  nickname: undefined;
  picture: string;
  email: undefined;
  locale: undefined;
  type: IdentityKind.AiAgent;
}

export const ANONYMOUS_USER = {
  accountId: ANONYMOUS_ID,
  displayName: undefined,
  nickname: undefined,
  picture: undefined,
  email: undefined,
  locale: undefined,
  type: IdentityKind.AnonymousId,
} satisfies Anonymous;

export const AI_AGENT_USER = {
  accountId: AGENT_ID,
  displayName: undefined,
  nickname: undefined,
  picture: rovoAvatarUrl,
  email: undefined,
  locale: undefined,
  type: IdentityKind.AiAgent,
} satisfies AiAgentIdentity;
/**
 * Represents the current user's identity state, which can either be
 * - authenticated
 * - anonymous
 * Differs from Identity as there's no partial state for your own identity (it's all or nothing)
 */
export type UserIdentity = CanvasIdentity | Anonymous | AiAgentIdentity;

/**
 * Unified identity type to encode the different types of identity we accept, including a partial state
 * if we can't fetch the full identity of an accountId
 */
export type Identity = UserIdentity | IdentityAccountIdOnly;
